/**
 * Owner-only post-login hub: training, Skylite, licenses, renewal queue, support impersonation.
 */
import React, { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import SchoolIcon from '@mui/icons-material/School';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { useLanguage } from '../contexts/LanguageContext.js';
import { getLearningDashboardUrl } from '../lib/learningDashboardUrl.js';
import TeacherPackMarketplace from './TeacherPackMarketplace.js';
import {
  clearAllOwnerPreviewSession,
  setImpersonationEnglishChrome
} from '../lib/institutionLeaderHubSession.js';

const HIDE_RENEWAL_KEY = 'skylite_owner_hide_license_renewal_v1';

function readHideRenewal() {
  try {
    return localStorage.getItem(HIDE_RENEWAL_KEY) === '1';
  } catch {
    return false;
  }
}

const fieldSx = {
  '& .MuiOutlinedInput-root': { backgroundColor: 'rgba(0,0,0,0.2)' },
  '& .MuiInputLabel-root': { color: 'rgba(255, 220, 180, 0.85)' },
  '& .MuiOutlinedInput-input': { color: '#fff8ec' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 200, 140, 0.45)' },
  '& .MuiFormHelperText-root': { color: 'rgba(230, 210, 180, 0.7)' }
};

export default function OwnerAdminDashboard({
  trainingUrl,
  onEnterSkyliteDesktop,
  onOpenLicenses,
  onOpenSupportTickets
}) {
  const { t } = useLanguage();
  const [hideRenewal, setHideRenewal] = useState(() => readHideRenewal());
  const [renewals, setRenewals] = useState([]);
  const [renewalsLoading, setRenewalsLoading] = useState(true);
  const [renewalsError, setRenewalsError] = useState('');

  const [school, setSchool] = useState('');
  const [instructorEmail, setInstructorEmail] = useState('');
  const [q, setQ] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [hits, setHits] = useState([]);
  const [people, setPeople] = useState([]);
  const [selectedBatchId, setSelectedBatchId] = useState(null);
  const [addSeatsCount, setAddSeatsCount] = useState('0');
  const [addingSeats, setAddingSeats] = useState(false);
  const [addSeatsMessage, setAddSeatsMessage] = useState('');
  const [addSeatsError, setAddSeatsError] = useState('');
  const [lastAddedCodes, setLastAddedCodes] = useState([]);
  const [lastAddedBatchId, setLastAddedBatchId] = useState(null);
  const [impersonateEmail, setImpersonateEmail] = useState('');
  const [impersonateHits, setImpersonateHits] = useState([]);
  const [impersonateBusy, setImpersonateBusy] = useState(false);
  const [impersonateError, setImpersonateError] = useState('');
  const [impersonateMessage, setImpersonateMessage] = useState('');
  const [marketplaceOpen, setMarketplaceOpen] = useState(false);
  const [enabledTrainingPacks, setEnabledTrainingPacks] = useState(['cloud-security']);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch('/api/session', { credentials: 'include' });
        const data = await r.json().catch(() => ({}));
        if (cancelled || !r.ok) return;
        const packs = Array.isArray(data.enabledTrainingPacks)
          ? data.enabledTrainingPacks
          : (Array.isArray(data.user?.enabledTrainingPacks) ? data.user.enabledTrainingPacks : null);
        if (packs) setEnabledTrainingPacks(packs);
      } catch (_) {
        /* keep owner default preview packs */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const closeMarketplace = () => setMarketplaceOpen(false);
    window.addEventListener('commandline-close-marketplace', closeMarketplace);
    return () => window.removeEventListener('commandline-close-marketplace', closeMarketplace);
  }, []);

  const loadRenewals = useCallback(async () => {
    setRenewalsLoading(true);
    setRenewalsError('');
    try {
      const r = await fetch('/api/admin/owner-license-renewals', {
        credentials: 'include',
        headers: { Accept: 'application/json' }
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setRenewalsError(data.error || 'Failed to load renewals');
        setRenewals([]);
        return;
      }
      setRenewals(Array.isArray(data.items) ? data.items : []);
    } catch {
      setRenewalsError('Network error loading renewals.');
      setRenewals([]);
    } finally {
      setRenewalsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRenewals();
  }, [loadRenewals]);

  const toggleHideRenewal = () => {
    const next = !hideRenewal;
    setHideRenewal(next);
    try {
      localStorage.setItem(HIDE_RENEWAL_KEY, next ? '1' : '0');
    } catch {
      /* ignore */
    }
  };

  const runSearch = async (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    setSearchError('');
    setSelectedBatchId(null);
    setPeople([]);
    const schoolQ = school.trim();
    const emailQ = instructorEmail.trim();
    const freeQ = q.trim();
    if (!schoolQ && !emailQ && !freeQ) {
      setSearchError('Enter a school name, instructor email, or search text.');
      setHits([]);
      return;
    }
    setSearching(true);
    try {
      const params = new URLSearchParams();
      if (schoolQ) params.set('school', schoolQ);
      if (emailQ) params.set('email', emailQ);
      if (freeQ) params.set('q', freeQ);
      const r = await fetch(`/api/admin/owner-support/search?${params.toString()}`, {
        credentials: 'include',
        headers: { Accept: 'application/json' }
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setSearchError(data.error ? String(data.error).replace(/_/g, ' ') : 'Search failed');
        setHits([]);
        setPeople([]);
        return;
      }
      const schoolHits = Array.isArray(data.items) ? data.items : [];
      const userHits = Array.isArray(data.users) ? data.users : [];
      setHits(schoolHits);
      setPeople(userHits);
      if (!schoolHits.length && !userHits.length) setSearchError('No schools or users matched.');
    } catch {
      setSearchError('Network error.');
      setHits([]);
      setPeople([]);
    } finally {
      setSearching(false);
    }
  };

  const selected = hits.find((h) => h.batchId === selectedBatchId) || null;

  const lookupImpersonateUser = async (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    setImpersonateError('');
    setImpersonateMessage('');
    setImpersonateHits([]);
    const emailQ = impersonateEmail.trim().toLowerCase();
    if (!emailQ) {
      setImpersonateError('Enter a user email or student ID (e.g. 380389@students.lrsd.org or 380389).');
      return;
    }
    setImpersonateBusy(true);
    try {
      const params = new URLSearchParams({ email: emailQ });
      const r = await fetch(`/api/admin/impersonate/lookup?${params.toString()}`, {
        credentials: 'include',
        headers: { Accept: 'application/json' }
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setImpersonateError(data.error ? String(data.error).replace(/_/g, ' ') : 'Lookup failed');
        return;
      }
      const items = Array.isArray(data.items) ? data.items : [];
      setImpersonateHits(items);
      if (!items.length) setImpersonateError('No user found with that email.');
    } catch {
      setImpersonateError('Network error.');
    } finally {
      setImpersonateBusy(false);
    }
  };

  const startImpersonate = async (userId) => {
    if (!userId || impersonateBusy) return;
    setImpersonateBusy(true);
    setImpersonateError('');
    setImpersonateMessage('');
    try {
      const r = await fetch('/api/admin/impersonate', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ userId })
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setImpersonateError(data.error ? String(data.error).replace(/_/g, ' ') : 'Impersonate failed');
        return;
      }
      clearAllOwnerPreviewSession();
      const role = String(data.user?.role || data.impersonation?.targetRole || '').toLowerCase();
      setImpersonateMessage(`Signed in as ${data.user?.email || userId}…`);
      try {
        setImpersonationEnglishChrome();
      } catch (_) {
        /* ignore */
      }
      if (role === 'student') {
        window.location.href = getLearningDashboardUrl({ openDashboard: true });
      } else {
        window.location.href = '/';
      }
    } catch {
      setImpersonateError('Network error starting impersonation.');
    } finally {
      setImpersonateBusy(false);
    }
  };

  const impersonateSelectedTeacher = async () => {
    if (!selected?.teacherUserId) {
      setImpersonateError(
        'No teacher account linked to this school row. Use Impersonate by email with their Google email.'
      );
      return;
    }
    await startImpersonate(selected.teacherUserId);
  };

  const addStudentSeats = async () => {
    setAddSeatsMessage('');
    setAddSeatsError('');
    setLastAddedCodes([]);
    setLastAddedBatchId(null);
    if (!selected || !selected.institutionId) {
      setAddSeatsError('Select a school/instructor row first.');
      return;
    }
    const n = parseInt(addSeatsCount, 10);
    if (!Number.isFinite(n) || n < 1 || n > 500) {
      setAddSeatsError('Enter a student seat count between 1 and 500.');
      return;
    }
    setAddingSeats(true);
    try {
      const r = await fetch(`/api/admin/institutions/${selected.institutionId}/add-student-seats`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          studentSeats: n,
          contactEmail: selected.contactEmail || instructorEmail.trim() || undefined,
          instructorName: selected.instructorName || undefined
        })
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setAddSeatsError(data.error ? String(data.error).replace(/_/g, ' ') : 'Failed to add seats');
        return;
      }
      const codes = Array.isArray(data.codes) ? data.codes : [];
      setLastAddedCodes(codes);
      setLastAddedBatchId(data.batch?.id || null);
      setAddSeatsCount('0');
      setAddSeatsMessage(
        `Added ${data.addedStudentSeats} student seat(s) for ${data.institutionName}. Seat cap ${data.previousSeatLimit} → ${data.newSeatLimit}.`
      );
      setHits((prev) =>
        prev.map((h) =>
          h.institutionId === selected.institutionId
            ? {
                ...h,
                currentSeatLimit: data.newSeatLimit,
                studentSeats: Number(h.studentSeats || 0) + Number(data.addedStudentSeats || 0)
              }
            : h
        )
      );
    } catch {
      setAddSeatsError('Network error adding seats.');
    } finally {
      setAddingSeats(false);
    }
  };

  const downloadAddedCodesCsv = async () => {
    if (!lastAddedBatchId) return;
    try {
      const r = await fetch(`/api/admin/license-batches/${lastAddedBatchId}/codes.csv`, {
        credentials: 'include'
      });
      if (!r.ok) {
        setAddSeatsError('CSV export failed.');
        return;
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const slug = String(selected?.institutionName || 'licenses')
        .trim()
        .replace(/[^\w\s-]+/g, '')
        .replace(/\s+/g, '-')
        .slice(0, 50);
      a.download = `${slug || 'licenses'}-added-seats-batch-${lastAddedBatchId}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setAddSeatsError('CSV export failed.');
    }
  };

  return marketplaceOpen ? (
    <Box
      className="notranslate"
      translate="no"
      sx={{ minHeight: 'calc(100dvh - 52px)', width: '100%', overflow: 'auto' }}
    >
      <TeacherPackMarketplace
        enabledPackIds={enabledTrainingPacks}
        institutionName="Owner preview"
        backLabel="Back to Admin Dashboard"
        onBack={() => setMarketplaceOpen(false)}
      />
    </Box>
  ) : (
    <Box
      className="notranslate"
      translate="no"
      sx={{
        minHeight: 'calc(100dvh - 52px)',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        px: 2,
        py: 4,
        background:
          'radial-gradient(ellipse 120% 80% at 50% 0%, rgba(30, 60, 110, 0.55) 0%, rgba(6, 10, 22, 1) 45%, #040810 100%)',
        color: '#e8f4ff'
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 960,
          display: 'flex',
          justifyContent: 'flex-end',
          mb: 1
        }}
      >
        <Tooltip title="CommandLine Marketplace — level packs & Skylite CTF boxes">
          <IconButton
            color="primary"
            aria-label="Open CommandLine Marketplace"
            onClick={() => setMarketplaceOpen(true)}
            sx={{
              border: '1px solid rgba(120, 200, 255, 0.35)',
              bgcolor: 'rgba(8, 20, 40, 0.65)',
              '&:hover': { bgcolor: 'rgba(30, 80, 140, 0.45)' }
            }}
          >
            <ShoppingCartIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <Typography variant="overline" sx={{ letterSpacing: '0.28em', color: 'rgba(140, 200, 255, 0.85)', fontWeight: 700 }}>
        {t('ownerConsoleOverline')}
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 800, mt: 1, mb: 0.5, textAlign: 'center' }}>
        {t('ownerHubTitle')}
      </Typography>
      <Typography variant="body2" sx={{ color: 'rgba(180, 210, 240, 0.75)', mb: 3, textAlign: 'center', maxWidth: 560 }}>
        {t('ownerHubIntro')}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 2,
          width: '100%',
          maxWidth: 960,
          mx: 'auto'
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 2,
            border: '1px solid rgba(120, 200, 255, 0.28)',
            background: 'linear-gradient(165deg, rgba(16, 28, 52, 0.95), rgba(8, 14, 28, 0.98))',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5
          }}
        >
          <DesktopWindowsIcon sx={{ fontSize: 40, color: '#7ecbff' }} />
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            {t('ownerCardSkyliteTitle')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(200, 226, 255, 0.8)', flex: 1 }}>
            {t('ownerCardSkyliteBody')}
          </Typography>
          <Button variant="contained" onClick={onEnterSkyliteDesktop} sx={{ textTransform: 'none', fontWeight: 700 }}>
            {t('ownerCardSkyliteCta')}
          </Button>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 2,
            border: '1px solid rgba(120, 200, 255, 0.28)',
            background: 'linear-gradient(165deg, rgba(16, 28, 52, 0.95), rgba(8, 14, 28, 0.98))',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5
          }}
        >
          <SchoolIcon sx={{ fontSize: 40, color: '#9ae6b4' }} />
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            {t('ownerCardTrainingTitle')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(200, 226, 255, 0.8)', flex: 1 }}>
            {t('ownerCardTrainingBody')}
          </Typography>
          <Button
            variant="outlined"
            href={trainingUrl}
            onClick={function clearOwnerEnglishChromeForTraining() {
              // Owner hub forces English display; clear so Basic Training restores saved language.
              try {
                window.__skyliteForceEnglishChrome = false;
                sessionStorage.removeItem('skylite_owner_hub_nav_intent');
                sessionStorage.removeItem('skylite_owner_desktop_mode');
                sessionStorage.removeItem('skylite_owner_institution_preview_active');
              } catch (_) { /* ignore */ }
            }}
            sx={{ textTransform: 'none', fontWeight: 700, borderColor: 'rgba(140, 220, 255, 0.5)', color: '#d4f1ff' }}
          >
            {t('ownerCardTrainingCta')}
          </Button>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 2,
            border: '1px solid rgba(120, 200, 255, 0.28)',
            background: 'linear-gradient(165deg, rgba(16, 28, 52, 0.95), rgba(8, 14, 28, 0.98))',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5
          }}
        >
          <VpnKeyIcon sx={{ fontSize: 40, color: '#f6d365' }} />
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            {t('ownerCardLicenseTitle')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(200, 226, 255, 0.8)', flex: 1 }}>
            {t('ownerCardLicenseBody')}
          </Typography>
          <Button
            variant="outlined"
            onClick={onOpenLicenses}
            sx={{ textTransform: 'none', fontWeight: 700, borderColor: 'rgba(246, 211, 101, 0.45)', color: '#fff3c4' }}
          >
            {t('ownerCardLicenseCta')}
          </Button>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 2,
            border: '1px solid rgba(120, 200, 255, 0.28)',
            background: 'linear-gradient(165deg, rgba(16, 28, 52, 0.95), rgba(8, 14, 28, 0.98))',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5
          }}
        >
          <SupportAgentIcon sx={{ fontSize: 40, color: '#f0abfc' }} />
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Support tickets
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(200, 226, 255, 0.8)', flex: 1 }}>
            Review student issues and recommendations. Mark tickets complete when fixed.
          </Typography>
          <Button
            variant="outlined"
            onClick={onOpenSupportTickets}
            sx={{ textTransform: 'none', fontWeight: 700, borderColor: 'rgba(240, 171, 252, 0.5)', color: '#f5d0fe' }}
          >
            Review tickets
          </Button>
        </Paper>
      </Box>

      <Paper
        elevation={0}
        sx={{
          mt: 3,
          p: 0,
          width: '100%',
          maxWidth: 960,
          borderRadius: 2,
          border: '1px solid rgba(120, 200, 255, 0.28)',
          background: 'linear-gradient(165deg, rgba(16, 28, 52, 0.95), rgba(8, 14, 28, 0.98))',
          overflow: 'hidden'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, p: 2, pb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#e8f4ff' }}>
            License renewal queue
          </Typography>
          <Button size="small" onClick={toggleHideRenewal} sx={{ textTransform: 'none', color: 'rgba(180, 210, 240, 0.9)' }}>
            {hideRenewal ? 'Show' : 'Hide'}
          </Button>
        </Box>
        {!hideRenewal ? (
          <>
            <Typography variant="caption" sx={{ display: 'block', px: 2, pb: 1.5, color: 'rgba(180, 210, 240, 0.75)' }}>
              Live institutional batches from the license console — owner only.
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'rgba(30, 50, 80, 0.6)' }}>
                    {['Institution', 'Instructor', 'Seats', 'Renew by', 'Status'].map((c) => (
                      <TableCell
                        key={c}
                        sx={{ fontWeight: 800, color: 'rgba(220, 240, 255, 0.95)', borderColor: 'rgba(120, 200, 255, 0.2)' }}
                      >
                        {c}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {renewalsLoading ? (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ color: 'rgba(200, 220, 240, 0.8)' }}>
                        Loading…
                      </TableCell>
                    </TableRow>
                  ) : renewalsError ? (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ color: '#ffb4a8' }}>
                        {renewalsError}
                      </TableCell>
                    </TableRow>
                  ) : renewals.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ color: 'rgba(200, 220, 240, 0.8)' }}>
                        No institutional license batches yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    renewals.map((row, ri) => (
                      <TableRow key={row.batchId} sx={{ bgcolor: ri % 2 ? 'rgba(12, 20, 40, 0.5)' : 'rgba(8, 14, 28, 0.35)' }}>
                        <TableCell sx={{ color: 'rgba(230, 244, 255, 0.92)', borderColor: 'rgba(120, 200, 255, 0.15)' }}>
                          {row.institution}
                        </TableCell>
                        <TableCell sx={{ color: 'rgba(230, 244, 255, 0.92)', borderColor: 'rgba(120, 200, 255, 0.15)' }}>
                          {row.instructorName || '—'}
                        </TableCell>
                        <TableCell sx={{ color: 'rgba(230, 244, 255, 0.92)', borderColor: 'rgba(120, 200, 255, 0.15)' }}>
                          {row.seats}
                        </TableCell>
                        <TableCell sx={{ color: 'rgba(230, 244, 255, 0.92)', borderColor: 'rgba(120, 200, 255, 0.15)' }}>
                          {row.renewBy}
                        </TableCell>
                        <TableCell sx={{ borderColor: 'rgba(120, 200, 255, 0.15)' }}>
                          <Chip
                            size="small"
                            label={row.status}
                            sx={{
                              height: 22,
                              fontWeight: 700,
                              bgcolor:
                                row.status === 'Action'
                                  ? 'rgba(220, 80, 60, 0.35)'
                                  : row.status === 'Due soon'
                                    ? 'rgba(245, 158, 11, 0.3)'
                                    : 'rgba(34, 197, 94, 0.25)',
                              color: '#e8f4ff'
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : (
          <Typography variant="body2" sx={{ px: 2, pb: 2, color: 'rgba(180, 210, 240, 0.7)' }}>
            Renewal queue hidden.
          </Typography>
        )}
      </Paper>

      <Paper
        elevation={0}
        sx={{
          mt: 3,
          p: 2.5,
          width: '100%',
          maxWidth: 960,
          borderRadius: 2,
          border: '1px solid rgba(246, 180, 100, 0.45)',
          background: 'linear-gradient(165deg, rgba(40, 28, 12, 0.92), rgba(22, 16, 8, 0.96))'
        }}
      >
        <Typography variant="overline" sx={{ color: 'rgba(255, 210, 150, 0.9)', fontWeight: 800, letterSpacing: '0.12em' }}>
          Support
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 800, mt: 0.5, mb: 1 }}>
          Impersonate teacher or student
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(230, 210, 180, 0.88)', mb: 2 }}>
          Signs you in as that account so you see exactly what they see (Learning Center, classes, licenses). Use
          Exit impersonation on the banner to return to your owner hub. Audit-logged; 2-hour session.
        </Typography>

        <Box
          component="form"
          noValidate
          onSubmit={lookupImpersonateUser}
          sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start', mb: 2 }}
        >
          <TextField
            size="small"
            label="User email or student ID"
            type="text"
            value={impersonateEmail}
            onChange={(e) => setImpersonateEmail(e.target.value)}
            placeholder="e.g. 380389@students.lrsd.org, 380389, or regina.howard@…"
            InputLabelProps={{ shrink: true }}
            sx={{ ...fieldSx, minWidth: 280, flex: '1 1 240px' }}
          />
          <Button
            type="submit"
            variant="contained"
            disabled={impersonateBusy}
            sx={{ textTransform: 'none', fontWeight: 800, backgroundColor: '#c2410c', '&:hover': { backgroundColor: '#9a3412' } }}
          >
            {impersonateBusy ? 'Working…' : 'Look up'}
          </Button>
        </Box>
        {impersonateError ? (
          <Typography variant="body2" sx={{ color: '#ffb4a8', mb: 1.5 }}>
            {impersonateError}
          </Typography>
        ) : null}
        {impersonateMessage ? (
          <Typography variant="body2" sx={{ color: '#9ae6b4', mb: 1.5 }}>
            {impersonateMessage}
          </Typography>
        ) : null}
        {impersonateHits.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
            {impersonateHits.map((u) => (
              <Paper
                key={u.userId}
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  border: '1px solid rgba(255, 200, 140, 0.35)',
                  bgcolor: 'rgba(0,0,0,0.28)',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 800, color: '#fff8ec' }}>
                    {u.name || 'User'} · {u.email}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(230, 210, 180, 0.8)' }}>
                    id {u.userId}
                    {u.appRole ? ` · app ${u.appRole}` : ''}
                    {u.roleAtInstitution ? ` · school ${u.roleAtInstitution}` : ''}
                    {u.institutionName ? ` · ${u.institutionName}` : ''}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="contained"
                  disabled={impersonateBusy}
                  onClick={() => startImpersonate(u.userId)}
                  sx={{ textTransform: 'none', fontWeight: 800 }}
                >
                  Impersonate
                </Button>
              </Paper>
            ))}
          </Box>
        ) : null}

        <Typography variant="subtitle2" sx={{ fontWeight: 800, mt: 1, mb: 1, color: '#ffe8cc' }}>
          Find school / instructor (licenses + teacher impersonate)
        </Typography>
        <Box
          component="form"
          onSubmit={runSearch}
          sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mb: 1.5 }}
        >
          <TextField
            size="small"
            label="School"
            value={school}
            onChange={(e) => setSchool(e.target.value)}
            placeholder="e.g. Metro Technical"
            InputLabelProps={{ shrink: true }}
            sx={fieldSx}
          />
          <TextField
            size="small"
            label="Instructor email"
            type="email"
            value={instructorEmail}
            onChange={(e) => setInstructorEmail(e.target.value)}
            placeholder="e.g. regina.howard@lrsd.org"
            InputLabelProps={{ shrink: true }}
            sx={fieldSx}
          />
          <TextField
            size="small"
            label="Search all"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Full email or ID before @ (389091 or 389091@students.lrsd.org)"
            InputLabelProps={{ shrink: true }}
            sx={{ ...fieldSx, gridColumn: { sm: 'span 2' } }}
          />
          <Button
            type="submit"
            variant="outlined"
            disabled={searching}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              borderColor: 'rgba(255, 200, 140, 0.55)',
              color: '#ffe8cc',
              gridColumn: { sm: 'span 2' },
              justifySelf: 'start'
            }}
          >
            {searching ? 'Searching…' : 'Search schools'}
          </Button>
        </Box>
        {searchError ? (
          <Typography variant="body2" sx={{ color: '#ffb4a8', mb: 1.5 }}>
            {searchError}
          </Typography>
        ) : null}

        {people.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
            {people.map((u) => (
              <Paper
                key={u.userId}
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  border: '1px solid rgba(255, 200, 140, 0.35)',
                  bgcolor: 'rgba(0,0,0,0.28)',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 800, color: '#fff8ec' }}>
                    {u.name || 'User'} · {u.email}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(230, 210, 180, 0.8)' }}>
                    {u.appRole || 'student'}
                    {u.roleAtInstitution ? ` · ${u.roleAtInstitution}` : ''}
                    {u.institutionName ? ` · ${u.institutionName}` : ''}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="contained"
                  disabled={impersonateBusy}
                  onClick={() => startImpersonate(u.userId)}
                  sx={{ textTransform: 'none', fontWeight: 800 }}
                >
                  Impersonate
                </Button>
              </Paper>
            ))}
          </Box>
        ) : null}

        {hits.length > 0 ? (
          <TableContainer sx={{ mb: 2, maxHeight: 280 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {['School', 'Instructor', 'Email', 'Total seats', 'Seat cap'].map((c) => (
                    <TableCell key={c} sx={{ fontWeight: 800, bgcolor: 'rgba(40, 28, 12, 0.98)', color: '#ffe8cc' }}>
                      {c}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {hits.map((h) => {
                  const selectedRow = h.batchId === selectedBatchId;
                  return (
                    <TableRow
                      key={h.batchId}
                      hover
                      selected={selectedRow}
                      onClick={() => {
                        setSelectedBatchId(h.batchId);
                        setAddSeatsMessage('');
                        setAddSeatsError('');
                        setLastAddedCodes([]);
                        setLastAddedBatchId(null);
                      }}
                      sx={{ cursor: 'pointer', bgcolor: selectedRow ? 'rgba(194, 65, 12, 0.25)' : 'transparent' }}
                    >
                      <TableCell sx={{ color: '#fff8ec' }}>{h.institutionName}</TableCell>
                      <TableCell sx={{ color: '#fff8ec' }}>{h.instructorName || '—'}</TableCell>
                      <TableCell sx={{ color: '#fff8ec' }}>{h.contactEmail || '—'}</TableCell>
                      <TableCell sx={{ color: '#fff8ec' }}>
                        {h.adminSeats}A / {h.teacherSeats}T / {h.studentSeats}S
                      </TableCell>
                      <TableCell sx={{ color: '#fff8ec' }}>
                        {h.currentSeatLimit != null
                          ? `${h.activeStudents || 0} / ${h.currentSeatLimit}`
                          : `${h.activeStudents || 0} / —`}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : null}

        {selected ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              <Button
                variant="contained"
                disabled={impersonateBusy || !selected.teacherUserId}
                onClick={impersonateSelectedTeacher}
                sx={{ textTransform: 'none', fontWeight: 800 }}
              >
                Impersonate instructor
              </Button>
            </Box>

            <Paper
              elevation={0}
              sx={{
                p: 2,
                mt: 0.5,
                borderRadius: 1.5,
                border: '1px solid rgba(120, 200, 255, 0.35)',
                background: 'rgba(8, 18, 36, 0.65)'
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#a8e6ff', mb: 0.5 }}>
                Add student licenses
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(200, 226, 255, 0.8)', mb: 1.5 }}>
                Increases the school seat cap and issues that many new student codes. Does not replace the existing
                license total.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                <TextField
                  size="small"
                  label="Student seats to add"
                  type="number"
                  value={addSeatsCount}
                  onChange={(e) => setAddSeatsCount(e.target.value)}
                  inputProps={{ min: 1, max: 500 }}
                  sx={{ ...fieldSx, width: 180 }}
                />
                <Button
                  variant="contained"
                  disabled={addingSeats || !selected.institutionId}
                  onClick={addStudentSeats}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 800,
                    backgroundColor: '#0ea5e9',
                    '&:hover': { backgroundColor: '#0284c7' }
                  }}
                >
                  {addingSeats ? 'Adding…' : 'Add licenses'}
                </Button>
                {lastAddedBatchId ? (
                  <Button
                    variant="outlined"
                    onClick={downloadAddedCodesCsv}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      borderColor: 'rgba(120, 200, 255, 0.55)',
                      color: '#cfefff'
                    }}
                  >
                    Download codes CSV
                  </Button>
                ) : null}
              </Box>
              {addSeatsError ? (
                <Typography variant="body2" sx={{ color: '#ffb4a8', mt: 1.25 }}>
                  {addSeatsError}
                </Typography>
              ) : null}
              {addSeatsMessage ? (
                <Typography variant="body2" sx={{ color: '#9ae6b4', mt: 1.25 }}>
                  {addSeatsMessage}
                </Typography>
              ) : null}
              {lastAddedCodes.length > 0 ? (
                <Box
                  component="pre"
                  sx={{
                    mt: 1.25,
                    p: 1.25,
                    maxHeight: 160,
                    overflow: 'auto',
                    borderRadius: 1,
                    bgcolor: 'rgba(0,0,0,0.35)',
                    color: '#dff7ff',
                    fontSize: 12,
                    m: 0
                  }}
                >
                  {lastAddedCodes.map((c) => c.licenseCode).join('\n')}
                </Box>
              ) : null}
            </Paper>
          </Box>
        ) : null}
      </Paper>
    </Box>
  );
}
